import { FORM_ERROR } from "final-form";
import { Field, Form } from "react-final-form";

import { authorizeUser } from "@/api/endpoints/user";
import ArrowRight from "@/assets/icons/arrow-right.svg";
import useSearchParams from "@/hooks/useSearchParams";
import { ROUTES } from "@/routes_new";
import {
  EMAIL_REGEX,
  sequenceValidators,
  validateMatches,
  validateRequired,
} from "@/utils";
import GoogleButton from "@/views/signup/partials/GoogleButton";
import MicrosoftButton from "@/views/signup/partials/MicrosoftButton";
import Button from "@components/Button";
import { FF_InputField, FF_PasswordField } from "@components/Field";
import HelperText from "@components/Field/HelperText";
import Typography from "@components/Typography";

import styles from "./Login.module.css";
import { i18n } from "@lingui/core";

export type LoginData = {
  username: string;
  password: string;
};
export type LoginProps = {
  onLogin: (d: LoginData) => void | Promise<FormData>;
};

const Login: React.FunctionComponent<LoginProps> = ({ onLogin }) => {
  const [getParams] = useSearchParams<{ error?: string }>();
  // When SSO sends us back to this page we have no idea what happened.
  // So we're passing along a error param to indicate that something went wrong.
  const ssoError = getParams?.error;

  return (
    <>
      <div className={styles.root}>
        <GoogleButton
          href={ROUTES.LOGIN_SSO_GOOGLE}
          data-testid="login-sso-google"
        >
          {L.LOGIN_CTA_GOOGLE}
        </GoogleButton>
        <MicrosoftButton
          href={ROUTES.LOGIN_SSO_AZURE}
          data-testid="login-sso-azure"
        >
          {L.LOGIN_CTA_MICROSOFT}
        </MicrosoftButton>
        <Typography className={styles.divider}>{L.LOGIN_DIVIDER}</Typography>
      </div>
      <Form
        onSubmit={async (formValues: LoginData) =>
          authorizeUser(formValues).then((res) => {
            if (res.ok) {
              onLogin(formValues);
              return;
            }

            if (res.status < 500) {
              return {
                [FORM_ERROR]: (res.data as { error: string }).error,
              };
            }
            return res;
          })
        }
      >
        {({
          handleSubmit,
          submitting,
          invalid,
          submitError,
          dirty,
          hasValidationErrors,
          dirtySinceLastSubmit,
          pristine,
        }) => (
          <form
            onSubmit={handleSubmit}
            className={styles.form}
            data-testid="login-form"
          >
            <div className={styles.root}>
              <Field
                data-testid="login-username"
                block
                autoComplete="email"
                name="username"
                placeholder={L.LOGIN_EMAIL_PLACEHOLDER}
                label={L.LOGIN_EMAIL}
                validate={sequenceValidators(
                  validateRequired(i18n._(L.REQUIRED_FIELD_VALUE)),
                  validateMatches(i18n._(L.INVALID_EMAIL), EMAIL_REGEX)
                )}
                type="text"
                component={FF_InputField}
              />
              <Field
                block
                autoComplete="password"
                data-testid="login-password"
                name="password"
                placeholder={L.LOGIN_PASSWORD_PLACEHOLDER}
                label={L.LOGIN_PASSWORD}
                validate={validateRequired(i18n._(L.REQUIRED_FIELD_VALUE))}
                component={FF_PasswordField}
              />
              {submitError && !dirtySinceLastSubmit && (
                <HelperText error content={submitError} />
              )}
              {!dirty && ssoError && (
                <HelperText
                  data-testid="oauth-login-error"
                  error
                  content={
                    ssoError === "personal-email" ? (
                      L.OAUTH_PERSONAL_EMAIL_ERROR
                    ) : (
                      <>
                        {L.OAUTH_LOGIN_FAIL_ALERT_FIRST}{" "}
                        <a href="mailto:support@allears.ai">
                          {L.OAUTH_LOGIN_FAIL_ALERT_A_TAG}
                        </a>
                        {L.OAUTH_LOGIN_FAIL_ALERT_LAST}
                      </>
                    )
                  }
                />
              )}
            </div>
            <Button
              block
              data-testid="login-submit"
              type="submit"
              className={styles.login}
              disabled={
                !pristine && // Don't disable the form if it hasn't been touched at all. This allows autofill from chrome to work without extra clicks
                (submitting || // the user is not submitting right now
                  ((hasValidationErrors || invalid) && !dirtySinceLastSubmit)) // The user has something that's wrong in the form and hasn't changed anything since their last post
              }
              icon={ArrowRight}
              iconPlacement="after"
            >
              {L.LOGIN_CTA}
            </Button>
          </form>
        )}
      </Form>
      <div className={styles.root}>
        <Button
          block
          variant="tertiary"
          data-testid="login-forgot-password"
          href={ROUTES.FORGOT_PASSWORD}
        >
          {L.LOGIN_FORGOT_PASSWORD}
        </Button>
      </div>
      {L.OAUTH_LOGIN_FAIL_ALERT}
    </>
  );
};

export default Login;
